import type { AdminReportDetailDto } from '@carcheck/shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DefectsSection } from './DefectsSection';
import { RedactedField } from '../../../components/pii';
import { tokens } from '../../../design';

/**
 * Report detail tab body — order ref + inspector / customer embeds +
 * report content + validation summary (AUT-127). Mirrors the orders
 * `OverviewTab` shape — section title + label-value grid.
 *
 * Inspector / customer PII (name + phone + email) renders through
 * `<RedactedField perm="view_pii">`; the server's `piiRevealed` flag
 * agrees with the same permission.
 *
 * Validation summary surfaces the auto-validator's signal (passed +
 * key boolean flags). When `validation.passed === true`, a single
 * green Alert. When `false`, a warning Alert + the failed criteria
 * (the wire DTO doesn't include a list of individual failures — just
 * the booleans — so we render the booleans).
 */
export interface DetailsTabProps {
  detail: AdminReportDetailDto;
}

const SectionTitle = ({ children }: { children: ReactNode }): ReactElement => (
  <Typography
    sx={{
      fontSize: 11,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.6px',
      color: tokens.text.tertiary,
      mb: 1.25,
    }}
  >
    {children}
  </Typography>
);

const InfoItem = ({
  label,
  children,
  span,
}: {
  label: string;
  children: ReactNode;
  span?: boolean;
}): ReactElement => (
  <Box sx={span ? { gridColumn: '1 / -1' } : undefined}>
    <Typography sx={{ fontSize: 11, color: tokens.text.tertiary, mb: 0.4 }}>{label}</Typography>
    <Box sx={{ fontSize: 14, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {children}
    </Box>
  </Box>
);

const InfoGrid = ({ children }: { children: ReactNode }): ReactElement => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 24px' }}>{children}</Box>
);

const NoValue = ({ t }: { t: ReturnType<typeof useTranslation>['t'] }): ReactElement => (
  <Typography variant="body2" sx={{ color: tokens.text.tertiary }} component="span">
    {t('reports.detail.noValue')}
  </Typography>
);

const PiiText = ({
  value,
  revealed,
}: {
  value: string | null;
  revealed: boolean;
}): ReactElement => {
  const { t } = useTranslation();
  if (!value) return <NoValue t={t} />;
  return (
    <Typography component="span">
      <RedactedField
        perm="view_pii"
        value={value}
        redacted={value}
        {...(revealed ? { copyable: true } : {})}
      />
    </Typography>
  );
};

/** Order ref + inspector + customer embed sections. */
const PartiesSection = ({ detail }: DetailsTabProps): ReactElement => {
  const { t } = useTranslation();
  // AUT-372 — an order-less (picker) report has neither an order nor a
  // marketplace customer; those sections are simply not rendered.
  const { order, customer } = detail;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {order !== null && (
        <Box>
          <SectionTitle>{t('reports.detailsTab.sections.order')}</SectionTitle>
          <InfoGrid>
            <InfoItem label={t('reports.detailsTab.fields.orderNumber')}>
              <Typography fontFamily="monospace">{`№ ${order.orderNumber}`}</Typography>
            </InfoItem>
            <InfoItem label={t('reports.detailsTab.fields.orderStatus')}>
              <Typography>{order.status}</Typography>
            </InfoItem>
          </InfoGrid>
        </Box>
      )}
      <Box>
        <SectionTitle>{t('reports.detailsTab.sections.inspector')}</SectionTitle>
        <InfoGrid>
          <InfoItem label={t('reports.detailsTab.fields.name')}>
            <PiiText value={detail.inspector.name} revealed={detail.piiRevealed} />
          </InfoItem>
          <InfoItem label={t('reports.detailsTab.fields.phone')}>
            <PiiText value={detail.inspector.phone} revealed={detail.piiRevealed} />
          </InfoItem>
          <InfoItem label={t('reports.detailsTab.fields.email')} span>
            <PiiText value={detail.inspector.email} revealed={detail.piiRevealed} />
          </InfoItem>
        </InfoGrid>
      </Box>
      {customer !== null && (
        <Box>
          <SectionTitle>{t('reports.detailsTab.sections.customer')}</SectionTitle>
          <InfoGrid>
            <InfoItem label={t('reports.detailsTab.fields.name')}>
              <PiiText value={customer.name} revealed={detail.piiRevealed} />
            </InfoItem>
            <InfoItem label={t('reports.detailsTab.fields.phone')}>
              <PiiText value={customer.phone} revealed={detail.piiRevealed} />
            </InfoItem>
            <InfoItem label={t('reports.detailsTab.fields.email')} span>
              <PiiText value={customer.email} revealed={detail.piiRevealed} />
            </InfoItem>
          </InfoGrid>
        </Box>
      )}
    </Box>
  );
};

/** Report content (text + scores + VIN + diagnostics). */
const ContentSection = ({ detail }: DetailsTabProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box>
      <SectionTitle>{t('reports.detailsTab.sections.content')}</SectionTitle>
      <InfoGrid>
        <InfoItem label={t('reports.detailsTab.fields.conditionScore')}>
          <Typography fontFamily="monospace">{detail.conditionScore ?? '—'}</Typography>
        </InfoItem>
        <InfoItem label={t('reports.detailsTab.fields.ratingScore')}>
          <Typography fontFamily="monospace">{detail.ratingScore ?? '—'}</Typography>
        </InfoItem>
        <InfoItem label={t('reports.detailsTab.fields.buyRecommendation')}>
          <Typography>
            {detail.buyRecommendation
              ? t(`reports.detailsTab.buyRecommendation.${detail.buyRecommendation}`, {
                  defaultValue: detail.buyRecommendation,
                })
              : '—'}
          </Typography>
        </InfoItem>
        <InfoItem label={t('reports.detailsTab.fields.vinReport')}>
          {detail.vinReport.url ? (
            <Link href={detail.vinReport.url} target="_blank" rel="noreferrer">
              {detail.vinReport.provider ?? t('reports.detailsTab.fields.vinReportLink')}
            </Link>
          ) : (
            <Typography sx={{ color: tokens.text.tertiary }}>—</Typography>
          )}
        </InfoItem>
        <InfoItem label={t('reports.detailsTab.fields.diagnostics')} span>
          <Typography variant="body2">
            {detail.diagnostics.performed
              ? t('reports.detailsTab.fields.diagnosticsPerformed', {
                  codes: detail.diagnostics.errorCodes.length,
                })
              : t('reports.detailsTab.fields.diagnosticsSkipped')}
          </Typography>
        </InfoItem>
        {detail.text.summary ? (
          <InfoItem label={t('reports.detailsTab.fields.summary')} span>
            <Typography variant="body2">{detail.text.summary}</Typography>
          </InfoItem>
        ) : null}
      </InfoGrid>
    </Box>
  );
};

/** Validation summary block — green Alert when passed, warning when not. */
const ValidationSection = ({ detail }: DetailsTabProps): ReactElement => {
  const { t } = useTranslation();
  const v = detail.validation;
  return (
    <Box>
      <SectionTitle>{t('reports.detailsTab.sections.validation')}</SectionTitle>
      <Alert severity={v.passed ? 'success' : 'warning'} sx={{ mb: 1 }}>
        {v.passed
          ? t('reports.detailsTab.validationPassed')
          : t('reports.detailsTab.validationFailed')}
      </Alert>
      <InfoGrid>
        <InfoItem label={t('reports.detailsTab.fields.totalPhotos')}>
          <Typography fontFamily="monospace">{v.totalPhotos}</Typography>
        </InfoItem>
        <InfoItem label={t('reports.detailsTab.fields.hasVideo')}>
          <Typography>{v.hasVideo ? '✓' : '—'}</Typography>
        </InfoItem>
        <InfoItem label={t('reports.detailsTab.fields.hasVinReport')}>
          <Typography>{v.hasVinReport ? '✓' : '—'}</Typography>
        </InfoItem>
        <InfoItem label={t('reports.detailsTab.fields.categoriesMet')}>
          <Typography>{v.categoriesMet ? '✓' : '—'}</Typography>
        </InfoItem>
      </InfoGrid>
    </Box>
  );
};

export const DetailsTab = ({ detail }: DetailsTabProps): ReactElement => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <PartiesSection detail={detail} />
    <ContentSection detail={detail} />
    <DefectsSection defects={detail.defects} />
    <ValidationSection detail={detail} />
  </Box>
);
